/**
 * ⭐ **새 일정을 열 때의 «기본 시각»** — 판정을 한 곳에 (2026-10-01).
 *
 * ## ⛔ 무엇이 문제였나
 *
 * 종전에는 «언제 열어도 그 날 09:00~10:00» 이었다. 그런데 2026-10-01 에 「회의실 ＋ 지난 시간이면
 * 저장을 막는다」(PR #2041)가 들어오면서 그 기본값이 **오후에 저장을 막았다**:
 * ```
 *   오후 4시에 「＋ 일정 추가」를 열면 기본이 오늘 09:00~10:00 ⇒ 끝(10:00)이 «지난 시간»
 *   ⇒ ⛔ 회의실을 켜는 순간 저장 단추가 막힌다 — 사용자는 시각을 «먼저» 바꿔야 했다
 * ```
 * ⇒ ★ 제가 그 결함을 「같은 main 인데 아침 7·5 · 오후 4·8」로 재다가 찾았다(정본 시험의 수가 갈렸다).
 *
 * ## ⇒ 규칙 — **오늘을 고른 때만 「다음 정시부터 1시간」**
 *
 * ```
 * ⓐ 고른 날이 «오늘»      ⇒ «다음 정시»부터 1시간   (16:20 ⇒ 17:00~18:00)
 * ⓑ 고른 날이 «오늘이 아님» ⇒ 종전대로 09:00~10:00
 * ```
 * ⚠ ⓑ 를 안 가르면 **과거 날짜 입력이 엉뚱해진다** — 월 보기에서 지난 날을 눌러 적는 것은
 *   정상 쓰임이고, 그때 「다음 정시」는 뜻이 없다.
 *
 * ## ⚠ 경계 셋 — 셋 다 시험이 시계를 못 박고 잰다
 *
 * ```
 * ① 23:40 에 열면 다음 정시가 «다음 날 00:00» 이다 ⇒ ★ «날짜도 함께» 넘긴다.
 *    ⛔ 오늘 23:00 으로 누르면 그것이 또 「지난 시간」이 되어 같은 결함이 된다
 *    ⛔ 그리고 «24:00 을 쓰지 않는다» — `T24:00` 은 «다음 날 0시»로 읽혀 TIME 칼럼에 00:00 으로
 *      저장된 적이 있다(이 레포가 겪은 일). ⇒ 날짜를 올리고 00:00 을 쓴다
 * ② 정각에 열면(16:00:00) 다음 정시는 «17:00» 이다 — 16:00 은 「이미 지난 1초」가 될 수 있다
 * ③ ⛔ 날짜·시각을 «로컬»로 만든다. `toISOString()` 으로 날짜를 만들면 KST 00:00~09:00 에
 *    «어제»가 된다(2026-10-01 에 그 꼴의 운영 결함이 둘 나왔다).
 *    ⇒ ★ 이 모듈은 `Date` 의 로컬 메서드만 쓴다 — `toISOString` 을 «한 번도» 쓰지 않는다
 * ```
 *
 * ## ⚠ 이 규칙의 출처
 *
 * 사용자 결정은 「권고를 따른다」까지였고, **「다음 정시부터 1시간」·경계 셋의 구체안은 관제의 권고**다.
 * ⇒ ★ 그러므로 사용자가 다르게 바라면 바뀔 수 있다 — 바뀌는 자리는 이 파일 하나다.
 */

/** 같은 «로컬» 날인가. ⛔ `toISOString()` 으로 견주지 않는다 — 새벽에 어제가 된다. */
export function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

/**
 * 새 일정 창의 기본 시작·종료.
 *
 * @param pickedDate 사용자가 고른 날(또는 보고 있는 날)
 * @param now        「지금」 — ⚠ 시험이 못 박을 수 있게 «인자»로 받는다. 안 주면 `new Date()`
 */
export function defaultEventRange(pickedDate: Date, now: Date = new Date()): { start: Date; end: Date } {
  if (!isSameLocalDay(pickedDate, now)) {
    // ⓑ 오늘이 아니면 종전 그대로 — 지난 날·앞날 입력의 뜻을 바꾸지 않는다.
    const start = new Date(pickedDate);
    start.setHours(9, 0, 0, 0);
    const end = new Date(pickedDate);
    end.setHours(10, 0, 0, 0);
    return { start, end };
  }

  /*
   * ⓐ 오늘 — «다음 정시»부터 1시간.
   * ★ 분·초·ms 를 0 으로 내린 «그 시각»에 1시간을 더한다 ⇒ 정각(16:00:00)에도 다음 정시(17:00)다.
   * ★ 그리고 23 시대면 `setHours(24)` 가 «다음 날 00:00» 로 넘어간다 — Date 가 날짜를 올려 준다.
   *   ⇒ 그래서 ⛔ 「24:00」이라는 글자를 만들 일이 «없다».
   */
  const start = new Date(now);
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 1);
  const end = new Date(start);
  end.setHours(end.getHours() + 1);
  return { start, end };
}
